/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { FormulaContextLoader, REPORT_JOB_BILL_OF_MATERIALS, ReportService } from '@gh/builder-shared';
import {
  BulkUpdateRestService,
  ComponentRequest,
  GtoUom,
  JobEstimateRestService,
  JobOrderDelivery,
  JobOrderMethod,
  reportIdField,
  TakeOffParamsRestService,
  UomRestService,
} from '@gh/builder-shared-data';
import { DictionaryService, InitViewService } from '@gh/common-shared';
import { EntityVersion, ID } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import { DialogRouteSnapshot } from '@gh/core-ui';
import { GridLayoutService } from '@gh/core-ui-grid';
import { expandObservable } from '@gh/core-util';
import { map$, switchMap$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import {
  createInitJeMappingContext,
  JE_CCC_ID,
  JeAuthorizeResponse,
  JeCcFilter,
  JeCcOrderDetails,
  JeCostCenter,
  jeCostCenterResponseFromDto,
  JeDetails,
  JeDetailsDirtyData,
  jeDetailsDirtyDataToDto,
  jeDetailsFromDto,
  jeDetailsFromDtoWithMappingContext,
  JeDetailsProcess,
  jeDetailsToEmptyDirtyData,
  jeFormulaVariableFromTopDto,
  JeGetComponentsResponse,
  jeGetComponentsResponseFromDto,
  JeGetCostCenterResponse,
  JeMappingContext,
  JeOrderForSchedule,
  JeOrderGenerated,
  JeTopRefItem,
  jeTopRefItemFromDto,
  JeUomRefItem,
  jeUomRefItemFromDto,
} from './job-estimate-details.model';

export interface JeOrderDetailsDialogConfig {
  jobId: ID;
  costCenterId: ID;
  dirtyData: JeDetailsDirtyData;
  jobNumber: string;
  siteAddress: string;
}

@Injectable()
export class JobEstimateDetailsBusinessService implements InitViewService {
  constructor(private gridLayoutService: GridLayoutService,
              private translations: TranslationService,
              private dictionaryService: DictionaryService,
              private jobEstimateRest: JobEstimateRestService,
              private bulkUpdateRest: BulkUpdateRestService,
              private uomRest: UomRestService,
              private topRest: TakeOffParamsRestService,
              private report: ReportService,
              private formulaContextLoader: FormulaContextLoader) {

  }

  initView(route: ActivatedRouteSnapshot): any {
    const { jobId } = route.params;

    return expandObservable({
      ccFilterDict: this.dictionaryService.getByEnum(JeCcFilter),
      sendOrderByDict: this.dictionaryService.getByEnum(JobOrderMethod),
      orderDeliveryByDict: this.dictionaryService.getByEnum(JobOrderDelivery),
      gtoUomDict: this.dictionaryService.getByEnum(GtoUom),
      jeComponentGridLayout: this.gridLayoutService.get('jeComponents'),
      jeItemGridLayout: this.gridLayoutService.get('jeItems'),
      colorSelectionGridLayout: this.gridLayoutService.get('jeColorSelections'),
      orderGridLayout: this.gridLayoutService.get('jeOrders'),
      details: this.get(jobId),
      reportUrl: this.report.generateUrl(REPORT_JOB_BILL_OF_MATERIALS, [reportIdField('Job', jobId)]),
    }).pipe(switchMap$((expanded) => expandObservable({
      ...expanded,
      formulaContext: this.formulaContextLoader.loadForEstimatingWithVariables(expanded.details.tops
        .map(jeFormulaVariableFromTopDto)),
    })));
  }

  initWithCostCenter(jobId: ID,
                     costCenterId: Maybe<ID>): Observable<JeDetailsProcess> {
    return this.get(jobId).pipe(
      switchMap$((details) => {
        if (costCenterId) {
          const mappingContext = createInitJeMappingContext(details);
          return this.loadCostCenter(jobId, costCenterId, jeDetailsToEmptyDirtyData(details), mappingContext).pipe(
            map$((cc) => ({
              ...details,
              initialCostCenterId: costCenterId,
              initialCostCenter: cc,
            })));
        } else {
          return [details];
        }
      }));
  }

  initOrderDetailsDialog(route: DialogRouteSnapshot<JeOrderDetailsDialogConfig>): Observable<JeCcOrderDetails> {
    const { jobId, costCenterId, dirtyData } = route.input;

    return this.getOrderDetails(jobId, costCenterId, dirtyData);
  }

  get(jobId: ID): Observable<JeDetails> {
    return this.jobEstimateRest.get(jobId).pipe(
      map$(jeDetailsFromDto),
      map$((details) => this.injectComponentCostCenter(details)));
  }

  loadCostCenter(jobId: ID,
                 costCenterId: ID,
                 dirtyData: JeDetailsDirtyData,
                 mappingContext: JeMappingContext): Observable<JeGetCostCenterResponse> {
    return this.jobEstimateRest.getCostCenter(
      jobId,
      costCenterId,
      jeDetailsDirtyDataToDto(dirtyData),
    ).pipe(map$((cc) => jeCostCenterResponseFromDto(costCenterId, cc, mappingContext)));
  }

  getOrderDetails(jobId: ID, costCenterId: ID, dirtyData: JeDetailsDirtyData): Observable<JeCcOrderDetails> {
    return this.jobEstimateRest.getCostCenterOrderDetails(
      jobId,
      costCenterId,
      jeDetailsDirtyDataToDto(dirtyData));
  }

  getComponents(jobId: ID,
                components: ComponentRequest[],
                mappingContext: JeMappingContext): Observable<JeGetComponentsResponse> {
    return this.jobEstimateRest.getComponents(jobId, components).pipe(
      map$((response) => jeGetComponentsResponseFromDto(response, mappingContext)));
  }

  generateItemIds(count: number): Observable<ID[]> {
    return this.jobEstimateRest.generateItemIds(count).pipe(map$((resp) => resp.jobItemIds));
  }

  /**
   * On process we load new page initialization data (as for general page initialization)
   * and initial cost center simultaneously.
   */
  process(jobId: ID,
          dirtyData: JeDetailsDirtyData,
          costCenterId: Maybe<ID>,
          mappingContext: JeMappingContext): Observable<JeDetailsProcess> {
    return this.jobEstimateRest.recalculate(jobId, jeDetailsDirtyDataToDto(dirtyData, true, false)).pipe(
      map$((resp) => jeDetailsFromDtoWithMappingContext(resp, mappingContext)),
      map$((details) => this.injectComponentCostCenter(details)),
      switchMap$((details) => {
        if (costCenterId) {
          return this.loadCostCenter(jobId, costCenterId, jeDetailsToEmptyDirtyData(details), mappingContext).pipe(
            map$((cc) => ({
              ...details,
              initialCostCenterId: costCenterId,
              initialCostCenter: cc,
            })));
        } else {
          return [details];
        }
      }));
  }

  recalculate(jobId: ID, dirtyData: JeDetailsDirtyData, mappingContext: JeMappingContext): Observable<JeDetails> {
    return this.jobEstimateRest.recalculate(jobId, jeDetailsDirtyDataToDto(dirtyData, false, false)).pipe(
      map$((resp) => jeDetailsFromDtoWithMappingContext(resp, mappingContext)),
      map$((details) => this.injectComponentCostCenter(details)));
  }

  reprice(jobId: ID, dirtyData: JeDetailsDirtyData, mappingContext: JeMappingContext): Observable<JeDetails> {
    return this.jobEstimateRest.recalculate(jobId, jeDetailsDirtyDataToDto(dirtyData, false, true)).pipe(
      map$((resp) => jeDetailsFromDtoWithMappingContext(resp, mappingContext)),
      map$((details) => this.injectComponentCostCenter(details)));
  }

  authorize(jobId: ID, version: EntityVersion): Observable<JeAuthorizeResponse> {
    return this.jobEstimateRest.authorize(jobId, version);
  }

  unauthorize(jobId: ID, version: EntityVersion): Observable<JeAuthorizeResponse> {
    return this.jobEstimateRest.unauthorize(jobId, version);
  }

  generateOrders(costCenterIds: ID[]): Observable<JeOrderGenerated[]> {
    return this.jobEstimateRest.generateOrders(costCenterIds);
  }

  getUom(uomId: ID): Observable<JeUomRefItem> {
    return this.uomRest.getShared(uomId).pipe(map$(jeUomRefItemFromDto));
  }

  getTop(topId: ID): Observable<JeTopRefItem> {
    return this.topRest.getShared(topId).pipe(map$(jeTopRefItemFromDto));
  }

  estimateOrders(jobId: ID, orderList: JeOrderForSchedule[]): Observable<any> {
    return this.jobEstimateRest.estimateOrders(jobId, this.orderListToDto(orderList));
  }

  orderListToDto(orderList: JeOrderForSchedule[]): any[] {
    return orderList.map((item) => {
      const res = {};
      for (const key in item) {
        if (!!item[key] || item[key] === 0) {
          res[key] = item[key];
        }
      }
      return res;
    });
  }

  private createComponentCc(memo?: string): JeCostCenter {
    return {
      jobCostCenterId: JE_CCC_ID,
      costCenterId: JE_CCC_ID,
      code: 0,
      name: this.translations.get('lbl_components'),
      version: 0,
      preloaded: true,
      jobColorSelection: [],
      suppliers: [],
      memo,
    };
  }

  private injectComponentCostCenter(details: JeDetails): JeDetails {
    return ({
      ...details,
      jobCostCenters: [this.createComponentCc(details.memo)].concat(details.jobCostCenters),
    });
  }
}
